import { Component, h, register } from './core.js';
import { renderIcon } from './icons.js';

/**
 * Uploader: a file picker with drag-and-drop, validation and an optional
 * selected-files list. It never uploads anything itself: the host listens
 * for `select`, sends the files wherever they go, and reports progress and
 * outcome back with setProgress / setStatus.
 *
 * new dfc.Uploader(container, {
 *   multiple: true, accept: [] | ['.pdf', 'image/*'], maxSize: 0 (bytes, 0 = no limit), maxFiles: 0,
 *   label: 'Add files', hint: null, dropzone: true, showList: true, disabled: false,
 *   autoClear: false        // remove files from the list as soon as they are handed to the host
 * })
 * Events: select { files: [{ id, file, name, size, type }] }, reject { file, reason: 'size'|'type'|'count', limit },
 *         remove { entry }, dragenter / dragleave, valueChanged { value: files, previous, source }
 * Methods: open(), clear(), remove(id), setProgress(id, 0..100), setStatus(id, 'done'|'error'|'uploading', message),
 *          files (getter)
 */
export class Uploader extends Component {
	static get componentName() { return 'uploader'; }

	defaults() {
		return { multiple: true, accept: [], maxSize: 0, maxFiles: 0, label: 'Add files', hint: null, dropzone: true, showList: true, disabled: false, autoClear: false, dropText: 'Drop files here or' };
	}

	render() {
		const o = this.options;
		this._entries = this._entries || [];
		this.el.className = 'dfc dfc-uploader' + (o.dropzone ? ' has-dropzone' : '') + (o.disabled ? ' is-disabled' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		this.input = h('input', { type: 'file', className: 'dfc-uploader-input', multiple: o.multiple ? true : null, accept: o.accept && o.accept.length ? o.accept.join(',') : null, disabled: o.disabled ? true : null, tabindex: '-1', 'aria-hidden': 'true' });
		this.button = h('button', { type: 'button', className: 'dfc-btn dfc-uploader-button', disabled: o.disabled ? true : null }, renderIcon('upload'), h('span', { text: o.label }));
		const hint = o.hint || Uploader._hint(o);
		this.zone = h('div', { className: 'dfc-uploader-zone', role: 'group', 'aria-label': o.label },
			o.dropzone ? h('span', { className: 'dfc-uploader-droptext', text: o.dropText }) : null,
			this.button,
			hint ? h('span', { className: 'dfc-uploader-hint', text: hint }) : null);
		this.el.appendChild(this.input);
		this.el.appendChild(this.zone);
		this.listEl = h('ul', { className: 'dfc-uploader-list', role: 'list', hidden: !o.showList });
		this.el.appendChild(this.listEl);
		this._renderList();
	}

	_renderList() {
		if (!this.listEl) return;
		this.listEl.innerHTML = '';
		this.listEl.hidden = !this.options.showList || !this._entries.length;
		this._entries.forEach((e) => {
			const li = h('li', { className: `dfc-uploader-item is-${e.status || 'selected'}`, dataset: { id: e.id }, role: 'listitem' },
				renderIcon(Uploader._iconFor(e), 'dfc-uploader-fileicon'),
				h('span', { className: 'dfc-uploader-name', text: e.name, title: e.name }),
				h('span', { className: 'dfc-uploader-size', text: Uploader.formatSize(e.size) }),
				e.status === 'uploading' ? h('span', { className: 'dfc-uploader-progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(e.progress || 0) }, h('span', { className: 'dfc-uploader-bar', style: { width: (e.progress || 0) + '%' } })) : null,
				e.status === 'done' ? renderIcon('check', 'dfc-uploader-state') : e.status === 'error' ? renderIcon('warning', 'dfc-uploader-state') : null,
				e.message ? h('span', { className: 'dfc-uploader-message', text: e.message }) : null,
				h('button', { type: 'button', className: 'dfc-uploader-remove', title: 'Remove', 'aria-label': 'Remove ' + e.name, dataset: { remove: e.id } }, renderIcon('xmark')));
			this.listEl.appendChild(li);
		});
	}

	bind() {
		this._onClick = (e) => {
			const rm = e.target.closest && e.target.closest('[data-remove]');
			if (rm && this.el.contains(rm)) { this.remove(rm.dataset.remove); return; }
			if (e.target.closest && e.target.closest('.dfc-uploader-button')) this.open();
		};
		this._onChange = () => { this._take(this.input.files); this.input.value = ''; };
		this._onDrag = (e) => {
			if (!this.options.dropzone || this.options.disabled) return;
			e.preventDefault();
			if (e.type === 'dragenter' || e.type === 'dragover') { if (!this.zone.classList.contains('is-dragover')) { this.zone.classList.add('is-dragover'); this.emit('dragenter', {}); } }
			else if (e.type === 'dragleave' && !this.zone.contains(e.relatedTarget)) { this.zone.classList.remove('is-dragover'); this.emit('dragleave', {}); }
			else if (e.type === 'drop') { this.zone.classList.remove('is-dragover'); this._take(e.dataTransfer && e.dataTransfer.files); }
		};
		this._onPaste = (e) => {
			if (this.options.disabled || !e.clipboardData) return;
			const files = [...e.clipboardData.files || []];
			if (files.length) { e.preventDefault(); this._take(files); }
		};
		this.el.addEventListener('click', this._onClick);
		this.el.addEventListener('change', this._onChangeDelegated = (e) => { if (e.target === this.input) this._onChange(); });
		['dragenter', 'dragover', 'dragleave', 'drop'].forEach((t) => this.el.addEventListener(t, this._onDrag));
		this.el.addEventListener('paste', this._onPaste);
	}

	unbind() {
		if (!this.el) return;
		this.el.removeEventListener('click', this._onClick);
		this.el.removeEventListener('change', this._onChangeDelegated);
		['dragenter', 'dragover', 'dragleave', 'drop'].forEach((t) => this.el.removeEventListener(t, this._onDrag));
		this.el.removeEventListener('paste', this._onPaste);
	}

	/** Validates and admits files; emits `select` with the accepted entries. */
	_take(fileList) {
		const o = this.options;
		const files = [...(fileList || [])];
		if (!files.length) return;
		const accepted = [];
		const roomFor = o.maxFiles ? Math.max(0, o.maxFiles - this._entries.length) : Infinity;
		for (const file of (o.multiple ? files : files.slice(0, 1))) {
			if (accepted.length >= roomFor) { this.emit('reject', { file, reason: 'count', limit: o.maxFiles }); continue; }
			if (o.maxSize && file.size > o.maxSize) { this.emit('reject', { file, reason: 'size', limit: o.maxSize }); continue; }
			if (!Uploader.accepts(file, o.accept)) { this.emit('reject', { file, reason: 'type', limit: o.accept }); continue; }
			accepted.push({ id: 'f' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7), file, name: file.name, size: file.size, type: file.type, status: 'selected', progress: 0 });
		}
		if (!accepted.length) return;
		if (!o.multiple) this._entries = [];
		this._entries.push(...accepted);
		this._renderList();
		const before = this._snapshot; this._snapshot = this.files;
		this.emit('select', { files: accepted.map((e) => ({ id: e.id, file: e.file, name: e.name, size: e.size, type: e.type })) });
		this._valueChanged(this._snapshot, before || [], 'user');
		if (o.autoClear) { this._entries = this._entries.filter((e) => !accepted.includes(e)); this._renderList(); }
	}

	open() { if (!this.options.disabled && this.input) this.input.click(); return this; }
	/** value = the current file entries (read-only snapshot; setting an empty value clears). */
	get value() { return this.files; }
	set value(v) { if (!v || (Array.isArray(v) && !v.length)) this.clear(); }
	get files() { return this._entries.map((e) => ({ id: e.id, file: e.file, name: e.name, size: e.size, type: e.type, status: e.status, progress: e.progress })); }
	clear() { const before = this.files; this._entries = []; this._snapshot = []; this._renderList(); this._valueChanged([], before, 'api'); return this; }
	remove(id) {
		const entry = this._entries.find((e) => e.id === id);
		if (!entry) return this;
		const before = this.files;
		this._entries = this._entries.filter((e) => e.id !== id);
		this._snapshot = this.files;
		this._renderList();
		this.emit('remove', { entry: { id: entry.id, file: entry.file, name: entry.name } });
		this._valueChanged(this._snapshot, before, 'user');
		return this;
	}
	setProgress(id, pct) {
		const e = this._entries.find((x) => x.id === id);
		if (e) { e.status = 'uploading'; e.progress = Math.max(0, Math.min(100, Number(pct) || 0)); this._renderList(); }
		return this;
	}
	setStatus(id, status, message) {
		const e = this._entries.find((x) => x.id === id);
		if (e) { e.status = status; e.message = message || ''; if (status === 'done') e.progress = 100; this._renderList(); }
		return this;
	}

	/** True when `file` matches an accept list of extensions and/or MIME patterns (empty list = anything). */
	static accepts(file, accept) {
		if (!accept || !accept.length) return true;
		const name = (file.name || '').toLowerCase(), type = (file.type || '').toLowerCase();
		return accept.some((a) => {
			a = String(a).trim().toLowerCase();
			if (a.startsWith('.')) return name.endsWith(a);
			if (a.endsWith('/*')) return type.startsWith(a.slice(0, -1));
			return type === a;
		});
	}
	static formatSize(bytes) {
		const n = Number(bytes) || 0;
		if (n < 1024) return n + ' B';
		if (n < 1048576) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + ' KB';
		if (n < 1073741824) return (n / 1048576).toFixed(1) + ' MB';
		return (n / 1073741824).toFixed(2) + ' GB';
	}
	static _hint(o) {
		const bits = [];
		if (o.accept && o.accept.length) bits.push(o.accept.join(', '));
		if (o.maxSize) bits.push('up to ' + Uploader.formatSize(o.maxSize));
		if (o.maxFiles) bits.push('max ' + o.maxFiles + ' file' + (o.maxFiles === 1 ? '' : 's'));
		return bits.join(' · ') || null;
	}
	static _iconFor(e) {
		const t = e.type || '';
		if (t.startsWith('image/')) return 'image';
		if (t === 'application/pdf' || /\.pdf$/i.test(e.name)) return 'document';
		return 'file';
	}
}

register('uploader', Uploader);
